import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, LogIn, Mail } from 'lucide-react';
import { PublicLayout } from '@/routes/_layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { api, apiClient, ApiError } from '@/lib/api';

export function meta() {
  return [{ title: 'Ingresar · Red de creadores de contenido Educrea' }];
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const emailClean = email.trim();
    if (!emailClean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
      setError('Ingresá un email válido.');
      return;
    }
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setLoading(true);
    try {
      await api('/api/v1/auth/login', {
        method: 'POST',
        body: { email: emailClean, password },
        withCredentials: true,
      });
      navigate('/affiliate');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('No pudimos iniciar sesión. Intentá de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function requestMagicLink(e: FormEvent) {
    e.preventDefault();
    setMagicError(null);
    setMagicSent(false);
    const emailClean = email.trim();
    if (!emailClean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
      setMagicError('Ingresá un email válido.');
      return;
    }

    setMagicLoading(true);
    try {
      await apiClient.requestMagicLink(emailClean);
      setMagicSent(true);
    } catch (err) {
      setMagicError(
        err instanceof ApiError ? err.message : 'No pudimos enviar el link. Intentá de nuevo.',
      );
    } finally {
      setMagicLoading(false);
    }
  }

  return (
    <PublicLayout>
      <div className="mx-auto flex max-w-md flex-col gap-6 px-6 py-16">
        <div className="text-center">
          <h1 className="text-foreground text-3xl font-bold">Ingresar a Red Educrea</h1>
          <p className="text-muted-foreground mt-2 text-sm">Acceso para creadores de contenido</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Con tu email y contraseña</CardTitle>
            <CardDescription>Acceso para creadores activos</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && (
                <p role="alert" className="text-error text-sm">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="h-4 w-4" />
                )}
                Ingresar
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ingresar con un link</CardTitle>
            <CardDescription>
              Te enviaremos un link de acceso. También sirve para crear tu contraseña si tu
              onboarding está pendiente.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={requestMagicLink} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="magic-email">Email</Label>
                <Input
                  id="magic-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {magicSent && (
                <p role="status" className="text-success text-sm">
                  Si el email corresponde a una cuenta habilitada, recibirás un link en breve.
                </p>
              )}
              {magicError && (
                <p role="alert" className="text-error text-sm">
                  {magicError}
                </p>
              )}
              <Button type="submit" variant="outline" disabled={magicLoading} className="w-full">
                {magicLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Enviarme un link
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
